// ─────────────────────────────────────────────
//  IncomingAlertPopup — vibration + popup notification card for the people an
//  alert targets.
//
//  • Emergency → targets every supervising doctor on today's roster + the AIC.
//  • Urgency   → targets only the supervisor of that specific room.
//
//  Targeting is resolved upstream (AlertContext sets targetIds); this component
//  just reacts to the alerts that target the CURRENT user: it shows a dismissible
//  card naming the room. Acknowledge clears the card (and stops the room card
//  blinking on the board). The few-second ALARM (sound + phone vibration) is
//  fired centrally in AlertContext for EVERY targeted user, so it isn't repeated
//  here (this popup is gated to multi-room supervisors only).
// ─────────────────────────────────────────────
import React from "react";
import { useAlertCenter } from "../context/AlertContext";
import tokens from "../theme/tokens";

const { color, radius, shadow, font, space } = tokens;

// `monitorsMultiple` — true only for a supervisor actively monitoring MORE THAN
// ONE room (or the AIC/admin, who monitor the whole board). A doctor performing
// a case in their single room is NOT shown the popup (they still see the board
// red + can acknowledge on the room card). Defaults false so a non-board mount
// never pops.
export default function IncomingAlertPopup({ monitorsMultiple = false }) {
  // EMERGENCY only. Urgency is silent + private — it surfaces on the room card in
  // My Rooms (the assigned doctor), never as a center-screen popup.
  const { myUnackEmergencies, acknowledge } = useAlertCenter();
  // Live emergencies that target me and that I (this user) haven't acknowledged
  // — per-user clear via acknowledgedBy. Gated to multi-room supervisors.
  const incoming = monitorsMultiple ? (myUnackEmergencies || []) : [];

  if (incoming.length === 0) return null;

  return (
    <div style={styles.wrap} role="alert" aria-live="assertive">
      {incoming.map((a) => (
        <div key={a.id} style={{ ...styles.card, borderLeft: `5px solid ${color.accent.error}` }}>
          <div style={styles.body}>
            <div style={styles.title}>🚨 EMERGENCY</div>
            <div style={styles.room}>{a.roomName || "Operating room"}</div>
          </div>
          <button style={styles.ack} onClick={() => acknowledge(a.id)}>Acknowledge</button>
        </div>
      ))}
    </div>
  );
}

const styles = {
  wrap: {
    position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)",
    zIndex: 1200, display: "flex", flexDirection: "column", gap: space.sm,
    width: "min(420px, calc(100vw - 24px))",
  },
  card: {
    display: "flex", alignItems: "center", gap: space.md,
    background: color.surface.card, borderRadius: radius.md, boxShadow: shadow.lg,
    padding: "12px 14px", animation: "atcPulse 1.2s ease-in-out infinite",
  },
  body: { flex: 1, minWidth: 0 },
  title: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.accent.error, letterSpacing: ".4px" },
  room: { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary },
  ack: {
    background: color.accent.error, border: "none", color: "#fff", borderRadius: radius.sm,
    padding: "8px 14px", fontSize: font.size.body, fontWeight: font.weight.bold, cursor: "pointer", whiteSpace: "nowrap",
  },
};
